import { useEffect, useRef, useState } from "react";
import { MdDeleteOutline, MdSearch, MdUpload, MdCheck } from "react-icons/md";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { BsLayoutTextSidebarReverse, BsStars } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { FiPlus, FiCpu, FiFileText, FiGlobe, FiMessageSquare, FiLayers, FiEdit2, FiX, FiMaximize2 } from "react-icons/fi";
import { RiErrorWarningLine } from "react-icons/ri";
import TrainingService from "../../services/training-group";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import { useNavigate } from "react-router-dom";
import { setInitial } from "../../store/slices/chatSlice";
import { useDispatch } from "react-redux";
import { useLang } from "../../context/LanguageContext";

const TABS = [
  {
    key: "transcription",
    labelEN: "Transcription",
    labelES: "Transcripción",
    icon: <FiFileText className="w-4 h-4" />,
    dot: "bg-sky-400",
  },
  {
    key: "translation",
    labelEN: "Translation",
    labelES: "Traducción",
    icon: <FiGlobe className="w-4 h-4" />,
    dot: "bg-emerald-400",
  },
  {
    key: "aiChat",
    labelEN: "AI Chat",
    labelES: "Chat IA",
    icon: <FiMessageSquare className="w-4 h-4" />,
    dot: "bg-violet-400",
  },
];

const TRANSLATIONS = {
  EN: {
    title: "AI Training Hub",
    subtitle: "Configure and manage AI training groups, prompts, and suggestions",
    selectGroup: "Select Group",
    selectGroupPlaceholder: "Choose a group...",
    typeGroupPlaceholder: "Enter group name...",
    groupLogo: "Group Logo",
    removeLogo: "Remove",
    groupConfig: "Group Configuration",
    presetsFor: "Workmodes for",
    addPreset: "Add Workmode",
    editPreset: "Edit Workmode",
    addPresetModalTitle: "Add Workmode Suggestion",
    editPresetModalTitle: "Edit Workmode Suggestion",
    presetTitle: "Workmode Title",
    presetDescription: "Description",
    presetPrompt: "Prompt Text",
    savePreset: "Save Workmode",
    errPresetText: "Prompt text is required",
    noPresets: "No workmodes yet",
    noPresetsHint: "Start adding suggestions for this group",
    presetTitlePlaceholder: "Title (e.g. Greeting, Tone)",
    presetDescPlaceholder: "Short description (e.g. Friendly greeting)",
    presetTextPlaceholder: "Prompt text (e.g. How are you?)",
    presetPlaceholder: "Enter workmode suggestion / prompt...",
    deleteGroup: "Delete Group",
    updateGroup: "Update Group",
    createGroup: "Create Group",
    toastCreated: "Group created successfully!",
    toastUpdated: "Group updated successfully!",
    toastDeleted: "Group deleted successfully",
    masterPrompt: "System Prompt",
    masterPromptHint: "Define AI behavior for this group",
    masterPromptPlaceholder: "Enter system prompt...",
    aiFramework: "AI Framework",
    aiFrameworkHint: "Define AI framework standards and rules for this group",
    addRule: "Add Rule",
    editRule: "Edit Rule",
    addRuleModalTitle: "Add Framework Rule",
    editRuleModalTitle: "Edit Framework Rule",
    ruleTitle: "Rule Title",
    ruleDescription: "Description",
    status: "Status",
    groupStatus: "Group Status",
    active: "Active",
    inactive: "Inactive",
    saveRule: "Save Rule",
    noFramework: "No framework rules yet",
    noFrameworkHint: "Add organization standards and rules for this group",
    ruleTitlePlaceholder: "Title (e.g. GPT-4, Standards)",
    ruleDescPlaceholder: "Description (e.g. OpenAI flagship model)",
    errRuleTitle: "Rule title is required",
    errRuleDesc: "Rule description is required",
    searchGroups: "Search groups...",
    allGroups: "All Groups",
    newGroup: "New Group",
    cancel: "Cancel",
    create: "Create",
    groupName: "Group Name",
    errGroupName: "Group name is required",
    errMasterPrompt: "System prompt is required",
    noGroupsFound: "No groups found",
    saving: "Saving...",
    deleteConfirm: "Delete",
    groups: "Groups",
    expandPrompt: "Expand",
    expandPromptHint: "Expand prompt editor",
    promptModalTitle: "System Prompt",
    promptModalSubtitle: "View and edit the complete system prompt for this group",
    done: "Done",
    characters: "characters",
    words: "words",
  },
  ES: {
    title: "Centro de Entrenamiento IA",
    subtitle: "Configura y gestiona grupos de entrenamiento, prompts y sugerencias",
    selectGroup: "Seleccionar Grupo",
    selectGroupPlaceholder: "Elige un grupo...",
    typeGroupPlaceholder: "Escribe el nombre del grupo...",
    groupLogo: "Logo del Grupo",
    removeLogo: "Eliminar",
    groupConfig: "Configuración del Grupo",
    presetsFor: "Workmodes para",
    addPreset: "Agregar Workmode",
    editPreset: "Editar Workmode",
    addPresetModalTitle: "Agregar Sugerencia de Workmode",
    editPresetModalTitle: "Editar Sugerencia de Workmode",
    presetTitle: "Título del Workmode",
    presetDescription: "Descripción",
    presetPrompt: "Texto del Prompt",
    savePreset: "Guardar Workmode",
    errPresetText: "El texto del prompt es obligatorio",
    noPresets: "Sin workmodes aún",
    noPresetsHint: "Comienza agregando sugerencias para este grupo",
    presetTitlePlaceholder: "Título (ej. Saludo, Tono)",
    presetDescPlaceholder: "Descripción corta (ej. Saludo amigable)",
    presetTextPlaceholder: "Texto del prompt (ej. ¿Cómo estás?)",
    presetPlaceholder: "Ingresa la sugerencia o prompt del workmode...",
    deleteGroup: "Eliminar Grupo",
    updateGroup: "Actualizar Grupo",
    createGroup: "Crear Grupo",
    toastCreated: "¡Grupo creado exitosamente!",
    toastUpdated: "¡Grupo actualizado exitosamente!",
    toastDeleted: "Grupo eliminado exitosamente",
    masterPrompt: "Prompt del Sistema",
    masterPromptHint: "Define el comportamiento de la IA para este grupo",
    masterPromptPlaceholder: "Ingresa el prompt del sistema...",
    aiFramework: "Marco de IA",
    aiFrameworkHint: "Define las normas y reglas del marco de IA para este grupo",
    addRule: "Agregar Regla",
    editRule: "Editar Regla",
    addRuleModalTitle: "Agregar Regla del Marco",
    editRuleModalTitle: "Editar Regla del Marco",
    ruleTitle: "Título de la Regla",
    ruleDescription: "Descripción",
    status: "Estado",
    groupStatus: "Estado del Grupo",
    active: "Activo",
    inactive: "Inactivo",
    saveRule: "Guardar Regla",
    noFramework: "Sin reglas del marco aún",
    noFrameworkHint: "Agrega normas y reglas de la organización para este grupo",
    ruleTitlePlaceholder: "Título (ej. GPT-4, Normas)",
    ruleDescPlaceholder: "Descripción (ej. Modelo insignia de OpenAI)",
    errRuleTitle: "El título de la regla es obligatorio",
    errRuleDesc: "La descripción de la regla es obligatoria",
    searchGroups: "Buscar grupos...",
    allGroups: "Todos los Grupos",
    newGroup: "Nuevo Grupo",
    cancel: "Cancelar",
    create: "Crear",
    groupName: "Nombre del Grupo",
    errGroupName: "El nombre del grupo es obligatorio",
    errMasterPrompt: "El prompt del sistema es obligatorio",
    noGroupsFound: "No se encontraron grupos",
    saving: "Guardando...",
    deleteConfirm: "Eliminar",
    groups: "Grupos",
    expandPrompt: "Expandir",
    expandPromptHint: "Expandir editor de prompt",
    promptModalTitle: "Prompt del Sistema",
    promptModalSubtitle: "Ver y editar el prompt del sistema completo para este grupo",
    done: "Listo",
    characters: "caracteres",
    words: "palabras",
  },
};

const emptyPresets = () => [];

const AiTraining = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = TRANSLATIONS[lang];

  const [loading, setLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(true);
  const [groupId, setGroupId] = useState("");
  const [groupData, setGroupData] = useState("");
  const groupIdRef = useRef("");
  groupIdRef.current = groupId;
  const [isOpen, setIsOpen] = useState(false);
  const [addGroupName, setAddGroupName] = useState("");
  const [showNewGroupPopup, setShowNewGroupPopup] = useState(false);
  const [newGroupCreating, setNewGroupCreating] = useState(false);
  const [newGroupErrors, setNewGroupErrors] = useState({});
  const [popupLogo, setPopupLogo] = useState("");
  const [popupLogoPreview, setPopupLogoPreview] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [masterPrompt, setMasterPrompt] = useState("");
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [frameworkItems, setFrameworkItems] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    title: "",
    description: "",
  });
  const [ruleFormErrors, setRuleFormErrors] = useState({});
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPresetIndex, setEditingPresetIndex] = useState(null);
  const [presetForm, setPresetForm] = useState({
    title: "",
    description: "",
    text: "",
    _id: null,
  });
  const [presetFormErrors, setPresetFormErrors] = useState({});
  const [savingPreset, setSavingPreset] = useState(false);
  const [groupStatus, setGroupStatus] = useState(true);
  const [savingRule, setSavingRule] = useState(false);
  const [promptSaved, setPromptSaved] = useState(true);
  const [isSubmiting, setSubmiting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trainingImg, setTrainingImg] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [groups, setGroups] = useState([]);
  const [activeTab, setActiveTab] = useState("transcription");
  const [presets, setPresets] = useState({
    transcription: emptyPresets(),
    translation: emptyPresets(),
    aiChat: emptyPresets(),
  });
  const [imageErrors, setImageErrors] = useState({});
  const [groupsListLoading, setGroupsListLoading] = useState(false);

  const toggleDropdown = () => setIsOpen((o) => !o);

  const handleGroupSelect = (option) => {
    groupIdRef.current = option?._id || "";
    setSelectedGroup(option?.groupName);
    setGroupId(option?._id || "");
    setGroupData(option || "");
    setIsEdit(true);
    setIsOpen(false);
    setGroupSearch("");
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTrainingImg(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePopupLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPopupLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => setPopupLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const closeNewGroupPopup = () => {
    setShowNewGroupPopup(false);
    setAddGroupName("");
    setPopupLogo("");
    setPopupLogoPreview(null);
    setMasterPrompt("");
    setFrameworkItems([]);
    setNewGroupErrors({});
  };

  const openAddRuleModal = () => {
    setEditingRuleIndex(null);
    setRuleForm({
      title: "",
      description: "",
    });
    setRuleFormErrors({});
    setShowRuleModal(true);
  };

  const openEditRuleModal = (index) => {
    const item = frameworkItems[index];
    if (!item) return;
    setEditingRuleIndex(index);
    setRuleForm({
      title: item.title || "",
      description: item.description || "",
    });
    setRuleFormErrors({});
    setShowRuleModal(true);
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setEditingRuleIndex(null);
    setRuleForm({
      title: "",
      description: "",
    });
    setRuleFormErrors({});
  };

  const syncFrameworkToBackend = async (updatedItems, explicitStatus = groupStatus) => {
    if (!groupId || !isEdit) return true;
    try {
      const validFramework = (updatedItems || [])
        .filter((i) => (i?.title && String(i.title).trim() !== "") || (i?.description && String(i.description).trim() !== ""))
        .map((i) => ({
          title: String(i?.title || "").trim(),
          description: String(i?.description || "").trim(),
          ...(i?._id ? { _id: i._id } : {}),
        }));
      const frameworkJson = JSON.stringify(validFramework);
      const frameworkText = validFramework
        .map((i) => (i.title ? `${i.title}: ${i.description}` : i.description))
        .join("\n");

      const formData = new FormData();
      if (trainingImg) formData.append("file", trainingImg);
      formData.append("groupName", selectedGroup);
      formData.append("masterPrompt", masterPrompt?.trim() || "");
      formData.append("status", explicitStatus);
      formData.append("aiFramework", frameworkJson);
      formData.append("framework", frameworkJson);
      formData.append("ai_framework", frameworkJson);
      formData.append("frameworkItems", frameworkJson);
      formData.append("frameworkText", frameworkText);

      const fieldMap = { transcription: "transcribe", translation: "translate", aiChat: "chat" };
      TABS.forEach(({ key }) => {
        const tabPresets = (presets[key] || [])
          .filter(
            (s) =>
              (s?.title && String(s.title).trim() !== "") ||
              (s?.description && String(s.description).trim() !== "") ||
              (s?.text && String(s.text).trim() !== "")
          )
          .map((s) => ({
            title: String(s?.title || "").trim(),
            description: String(s?.description || "").trim(),
            text: String(s?.text || "").trim(),
            ...(s?._id ? { _id: s._id } : {}),
          }));
        formData.append(fieldMap[key], JSON.stringify(tabPresets));
      });

      const response = await TrainingService?.updateGroup(groupId, formData);
      if (response) {
        setPromptSaved(true);
        return true;
      }
      return false;
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update");
      return false;
    }
  };

  const syncPresetsToBackend = async (updatedPresets) => {
    if (!groupId || !isEdit) return true;
    try {
      const validFramework = (frameworkItems || [])
        .filter((i) => (i?.title && String(i.title).trim() !== "") || (i?.description && String(i.description).trim() !== ""))
        .map((i) => ({
          title: String(i?.title || "").trim(),
          description: String(i?.description || "").trim(),
          ...(i?._id ? { _id: i._id } : {}),
        }));
      const frameworkJson = JSON.stringify(validFramework);
      const frameworkText = validFramework
        .map((i) => (i.title ? `${i.title}: ${i.description}` : i.description))
        .join("\n");

      const formData = new FormData();
      if (trainingImg) formData.append("file", trainingImg);
      formData.append("groupName", selectedGroup);
      formData.append("masterPrompt", masterPrompt?.trim() || "");
      formData.append("status", groupStatus);
      formData.append("aiFramework", frameworkJson);
      formData.append("framework", frameworkJson);
      formData.append("ai_framework", frameworkJson);
      formData.append("frameworkItems", frameworkJson);
      formData.append("frameworkText", frameworkText);

      const fieldMap = { transcription: "transcribe", translation: "translate", aiChat: "chat" };
      TABS.forEach(({ key }) => {
        const listToUse = (updatedPresets && updatedPresets[key]) ? updatedPresets[key] : (presets[key] || []);
        const tabPresets = listToUse
          .filter(
            (s) =>
              (s?.title && String(s.title).trim() !== "") ||
              (s?.description && String(s.description).trim() !== "") ||
              (s?.text && String(s.text).trim() !== "")
          )
          .map((s) => ({
            title: String(s?.title || "").trim(),
            description: String(s?.description || "").trim(),
            text: String(s?.text || "").trim(),
            ...(s?._id ? { _id: s._id } : {}),
          }));
        formData.append(fieldMap[key], JSON.stringify(tabPresets));
      });

      const response = await TrainingService?.updateGroup(groupId, formData);
      if (response) {
        setPromptSaved(true);
        return true;
      }
      return false;
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update");
      return false;
    }
  };

  const handleToggleGroupStatus = async () => {
    const nextStatus = !groupStatus;
    setGroupStatus(nextStatus);
    if (groupId && isEdit) {
      await syncFrameworkToBackend(frameworkItems, nextStatus);
      toastSuccess(nextStatus ? (lang === "ES" ? "¡Grupo activado!" : "Group activated!") : (lang === "ES" ? "¡Grupo desactivado!" : "Group deactivated!"));
    } else {
      setPromptSaved(false);
    }
  };

  const handleSaveRuleModal = async (e) => {
    if (e) e.preventDefault();
    const errors = {};
    if (!ruleForm.title.trim()) errors.title = t.errRuleTitle || "Rule title is required";
    if (!ruleForm.description.trim()) errors.description = t.errRuleDesc || "Rule description is required";

    if (Object.keys(errors).length > 0) {
      setRuleFormErrors(errors);
      return;
    }

    const newRule = {
      title: ruleForm.title.trim(),
      description: ruleForm.description.trim(),
    };

    let updatedList = [];
    if (editingRuleIndex !== null && editingRuleIndex >= 0 && editingRuleIndex < frameworkItems.length) {
      updatedList = [...frameworkItems];
      updatedList[editingRuleIndex] = { ...updatedList[editingRuleIndex], ...newRule };
    } else {
      updatedList = [...frameworkItems, newRule];
    }

    setFrameworkItems(updatedList);

    if (groupId && isEdit) {
      setSavingRule(true);
      const success = await syncFrameworkToBackend(updatedList);
      setSavingRule(false);
      if (success) {
        toastSuccess(editingRuleIndex !== null ? (lang === "ES" ? "¡Regla actualizada exitosamente!" : "Rule updated successfully!") : (lang === "ES" ? "¡Regla agregada exitosamente!" : "Rule added successfully!"));
      }
    } else {
      setPromptSaved(false);
    }

    closeRuleModal();
  };

  const handleAddFrameworkItem = () => {
    openAddRuleModal();
  };

  const handleFrameworkItemChange = (index, field, value) => {
    setFrameworkItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setPromptSaved(false);
  };

  const handleRemoveFrameworkItem = async (index) => {
    const item = frameworkItems[index];
    if (item?._id && groupId && isEdit) {
      try {
        await TrainingService.deleteSuggestion(groupId, "aiFramework", item._id);
      } catch (_) { }
    }
    const updated = frameworkItems.filter((_, i) => i !== index);
    setFrameworkItems(updated);
    if (groupId && isEdit) {
      await syncFrameworkToBackend(updated);
      toastSuccess(lang === "ES" ? "¡Regla eliminada!" : "Rule removed!");
    } else {
      setPromptSaved(false);
    }
  };

  const handleConfirmNewGroup = async () => {
    const errors = {};
    if (!addGroupName.trim()) errors.groupName = t.errGroupName;
    if (Object.keys(errors).length) {
      setNewGroupErrors(errors);
      return;
    }
    setNewGroupErrors({});
    setNewGroupCreating(true);
    try {
      const validFramework = (frameworkItems || [])
        .filter((i) => (i?.title && String(i.title).trim() !== "") || (i?.description && String(i.description).trim() !== ""))
        .map((i) => ({
          title: String(i?.title || "").trim(),
          description: String(i?.description || "").trim(),
        }));
      const frameworkJson = JSON.stringify(validFramework);
      const frameworkText = validFramework
        .map((i) => (i.title ? `${i.title}: ${i.description}` : i.description))
        .join("\n");

      const formData = new FormData();
      formData.append("groupName", addGroupName.trim());
      if (popupLogo) formData.append("file", popupLogo);
      formData.append("masterPrompt", masterPrompt?.trim() || "");
      formData.append("status", groupStatus);
      formData.append("aiFramework", frameworkJson);
      formData.append("framework", frameworkJson);
      formData.append("ai_framework", frameworkJson);
      formData.append("frameworkItems", frameworkJson);
      formData.append("frameworkText", frameworkText);

      const fieldMap = { transcription: "transcribe", translation: "translate", aiChat: "chat" };
      TABS.forEach(({ key }) => {
        const tabPresets = (presets[key] || [])
          .filter(
            (s) =>
              (s?.title && String(s.title).trim() !== "") ||
              (s?.description && String(s.description).trim() !== "") ||
              (s?.text && String(s.text).trim() !== "")
          )
          .map((s) => ({
            title: String(s?.title || "").trim(),
            description: String(s?.description || "").trim(),
            text: String(s?.text || "").trim(),
            ...(s?._id ? { _id: s._id } : {}),
          }));
        formData.append(fieldMap[key], JSON.stringify(tabPresets));
      });

      const response = await TrainingService?.createTrainingGroup(formData);
      if (response) {
        toastSuccess(t.toastCreated);
        setAddGroupName("");
        setPopupLogo("");
        setPopupLogoPreview(null);
        setMasterPrompt("");
        setFrameworkItems([]);
        setShowNewGroupPopup(false);
        const groupsRes = await TrainingService?.getAllGroup();
        if (groupsRes) {
          const raw = groupsRes?.data?.data?.groups || groupsRes?.data?.data;
          setGroups(Array.isArray(raw) ? raw : []);
          setSelectedGroup(addGroupName.trim());
        }
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setNewGroupCreating(false);
    }
  };

  const currentPresets = presets[activeTab];

  const openAddPresetModal = () => {
    setEditingPresetIndex(null);
    setPresetForm({
      title: "",
      description: "",
      text: "",
      _id: null,
    });
    setPresetFormErrors({});
    setShowPresetModal(true);
  };

  const openEditPresetModal = (index) => {
    const item = presets[activeTab][index];
    if (!item) return;
    setEditingPresetIndex(index);
    setPresetForm({
      title: item.title || "",
      description: item.description || "",
      text: item.text || "",
      _id: item._id || null,
    });
    setPresetFormErrors({});
    setShowPresetModal(true);
  };

  const closePresetModal = () => {
    setShowPresetModal(false);
    setEditingPresetIndex(null);
    setPresetForm({
      title: "",
      description: "",
      text: "",
      _id: null,
    });
    setPresetFormErrors({});
  };

  const handleSavePresetModal = async (e) => {
    if (e) e.preventDefault();
    const errors = {};
    if (!presetForm.text.trim() && !presetForm.title.trim()) {
      errors.text = t.errPresetText || "Prompt text or title is required";
    }

    if (Object.keys(errors).length > 0) {
      setPresetFormErrors(errors);
      return;
    }

    const newPreset = {
      title: presetForm.title.trim(),
      description: presetForm.description.trim(),
      text: presetForm.text.trim(),
      _id: presetForm._id || null,
    };

    const currentList = [...(presets[activeTab] || [])];
    let nextList;
    if (editingPresetIndex !== null && editingPresetIndex >= 0 && editingPresetIndex < currentList.length) {
      nextList = [...currentList];
      nextList[editingPresetIndex] = { ...nextList[editingPresetIndex], ...newPreset };
    } else {
      nextList = [newPreset, ...currentList];
    }

    const nextPresets = { ...presets, [activeTab]: nextList };
    setPresets(nextPresets);

    if (groupId && isEdit) {
      setSavingPreset(true);
      const success = await syncPresetsToBackend(nextPresets);
      setSavingPreset(false);
      if (success) {
        toastSuccess(
          editingPresetIndex !== null
            ? (lang === "ES" ? "¡Preset actualizado exitosamente!" : "Preset updated successfully!")
            : (lang === "ES" ? "¡Preset agregado exitosamente!" : "Preset added successfully!")
        );
      }
    } else {
      setPromptSaved(false);
      toastSuccess(
        editingPresetIndex !== null
          ? (lang === "ES" ? "¡Preset actualizado!" : "Preset updated!")
          : (lang === "ES" ? "¡Preset agregado!" : "Preset added!")
      );
    }

    closePresetModal();
  };

  const handleAddPreset = () => {
    openAddPresetModal();
  };

  const handleRemovePreset = async (index) => {
    const item = presets[activeTab][index];
    if (item?._id && groupId && isEdit) {
      const fieldMap = { transcription: "transcribe", translation: "translate", aiChat: "chat" };
      try {
        await TrainingService.deleteSuggestion(groupId, fieldMap[activeTab], item._id);
      } catch (error) {
        toastError(error?.response?.data?.message);
        return;
      }
    }
    const updated = presets[activeTab].filter((_, i) => i !== index);
    const nextPresets = { ...presets, [activeTab]: updated.length === 0 ? emptyPresets() : updated };
    setPresets(nextPresets);
    if (groupId && isEdit) {
      await syncPresetsToBackend(nextPresets);
      toastSuccess(lang === "ES" ? "¡Preset eliminado!" : "Preset deleted!");
    } else {
      setPromptSaved(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validFramework = (frameworkItems || [])
      .filter((i) => (i?.title && String(i.title).trim() !== "") || (i?.description && String(i.description).trim() !== ""))
      .map((i) => ({
        title: String(i?.title || "").trim(),
        description: String(i?.description || "").trim(),
      }));
    const frameworkJson = JSON.stringify(validFramework);
    const frameworkText = validFramework
      .map((i) => (i.title ? `${i.title}: ${i.description}` : i.description))
      .join("\n");

    const formData = new FormData();
    formData.append("file", trainingImg || "");
    formData.append("groupName", selectedGroup);
    formData.append("masterPrompt", masterPrompt?.trim() || "");
    formData.append("status", groupStatus);
    formData.append("aiFramework", frameworkJson);
    formData.append("framework", frameworkJson);
    formData.append("ai_framework", frameworkJson);
    formData.append("frameworkItems", frameworkJson);
    formData.append("frameworkText", frameworkText);

    const fieldMap = { transcription: "transcribe", translation: "translate", aiChat: "chat" };
    TABS.forEach(({ key }) => {
      const tabPresets = (presets[key] || [])
        .filter(
          (s) =>
            (s?.title && String(s.title).trim() !== "") ||
            (s?.description && String(s.description).trim() !== "") ||
            (s?.text && String(s.text).trim() !== "")
        )
        .map((s) => ({
          title: String(s?.title || "").trim(),
          description: String(s?.description || "").trim(),
          text: String(s?.text || "").trim(),
          ...(s?._id ? { _id: s._id } : {}),
        }));
      formData.append(fieldMap[key], JSON.stringify(tabPresets));
    });

    try {
      setSubmiting(true);
      let response;
      if (!isEdit) {
        response = await TrainingService?.createTrainingGroup(formData);
        if (response) { toastSuccess(t.toastCreated); navigate("/user-admin"); }
      } else {
        const currentId = groupId;
        const currentName = selectedGroup;
        response = await TrainingService?.updateGroup(currentId, formData);
        if (response) {
          toastSuccess(t.toastUpdated);
          await getAllGroups({ showLoader: false, preserveId: currentId, preserveName: currentName });
        }
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setSubmiting(false);
    }
  };

  const getAllGroups = async ({ showLoader = false, showListLoader = false, preserveId, preserveName } = {}) => {
    if (showLoader && (!groups || groups.length === 0)) setLoading(true);
    if (showListLoader) setGroupsListLoading(true);
    try {
      const response = await TrainingService?.getAllGroup();
      if (response) {
        const raw = response?.data?.data?.groups || response?.data?.data;
        const data = Array.isArray(raw) ? raw : [];
        setGroups(data);
        const idToKeep = preserveId || groupIdRef.current;
        const kept =
          (idToKeep && data.find((g) => String(g._id) === String(idToKeep))) ||
          (preserveName && data.find((g) => g.groupName === preserveName)) ||
          null;
        if (kept) {
          setSelectedGroup(kept.groupName);
          setGroupId(kept._id);
          setGroupData(kept);
        } else {
          setSelectedGroup(data[0]?.groupName || "");
        }
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      if (showLoader) setLoading(false);
      if (showListLoader) setGroupsListLoading(false);
    }
  };

  const robustJsonParse = (str) => {
    if (typeof str !== "string") return str;
    let s = str.trim();
    if (!s) return null;

    // 1. Direct JSON parse
    try {
      const p = JSON.parse(s);
      if (typeof p === "string" && (p.trim().startsWith("{") || p.trim().startsWith("["))) {
        return robustJsonParse(p);
      }
      return p;
    } catch (_) { }

    // 2. Unescape double-escaped strings like \"{\\\"title\\\": ...}\"
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      try {
        const unquoted = JSON.parse(s);
        if (typeof unquoted === "string") return robustJsonParse(unquoted);
        return unquoted;
      } catch (_) { }
    }

    // 3. Single quoted pseudo-JSON: {'title': '', ...} or [{'title': ...}]
    if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
      try {
        const fixed = s
          .replace(/'/g, '"')
          .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');
        return JSON.parse(fixed);
      } catch (_) { }
    }

    // 4. Regex extraction for malformed JSON with title/text/description/_id
    if (s.includes("title") || s.includes("text") || s.includes("description") || s.includes("prompt")) {
      try {
        const titleMatch = s.match(/['"]?title['"]?\s*:\s*['"]([^'"]*)['"]/i);
        const descMatch = s.match(/['"]?description['"]?\s*:\s*['"]([^'"]*)['"]/i);
        const textMatch = s.match(/['"]?(?:text|prompt|suggestion)['"]?\s*:\s*['"]([^'"]*)['"]/i);
        const idMatch = s.match(/['"]?_id['"]?\s*:\s*['"]([^'"]*)['"]/i);
        if (titleMatch || descMatch || textMatch) {
          return {
            title: titleMatch ? titleMatch[1] : "",
            description: descMatch ? descMatch[1] : "",
            text: textMatch ? textMatch[1] : "",
            _id: idMatch ? idMatch[1] : null,
          };
        }
      } catch (_) { }
    }

    return s;
  };

  const unwrapPresetValue = (input) => {
    if (!input) return null;
    let curr = input;

    if (typeof curr === "string") {
      curr = robustJsonParse(curr);
      if (!curr) return null;
    }

    if (Array.isArray(curr)) {
      return curr.flatMap(unwrapPresetValue).filter(Boolean);
    }

    if (typeof curr === "object" && curr !== null) {
      // Check if any property inside curr is itself a stringified JSON
      for (const key of ["text", "prompt", "suggestion", "title", "description", "value"]) {
        if (typeof curr[key] === "string" && (curr[key].includes("{") || curr[key].includes("["))) {
          const parsedNested = robustJsonParse(curr[key]);
          if (Array.isArray(parsedNested)) {
            return parsedNested.flatMap(unwrapPresetValue).filter(Boolean);
          }
          if (parsedNested && typeof parsedNested === "object") {
            curr = { ...curr, ...parsedNested };
          }
        }
      }

      let title = String(curr.title || curr.name || curr.label || "").trim();
      let description = String(curr.description || "").trim();
      let text = String(curr.text ?? curr.prompt ?? curr.suggestion ?? curr.value ?? "").trim();
      const id = curr._id || null;

      // Clean out any accidental raw JSON remnants
      if (title.startsWith("{") || title.startsWith("[")) title = "";
      if (description.startsWith("{") || description.startsWith("[")) description = "";
      if (text.startsWith("{") || text.startsWith("[")) text = "";

      // If title is empty and text has "Title: Text" format, split it
      if (!title && text) {
        const colonIdx = text.indexOf(":");
        if (colonIdx > -1 && colonIdx < 35 && !text.startsWith("http")) {
          title = text.substring(0, colonIdx).trim();
          text = text.substring(colonIdx + 1).trim();
        }
      }

      // If text is empty and title has "Title: Text" format
      if (!text && title) {
        const colonIdx = title.indexOf(":");
        if (colonIdx > -1 && colonIdx < 35 && !title.startsWith("http")) {
          text = title.substring(colonIdx + 1).trim();
          title = title.substring(0, colonIdx).trim();
        }
      }

      // If both title and text are empty, but description is present
      if (!title && !text && description) {
        title = description;
      }

      // If all are empty, do not render a blank useless item
      if (!title && !description && !text) return null;

      return {
        title,
        description,
        text,
        _id: id,
      };
    }

    if (typeof curr === "string") {
      const trimmed = curr.trim();
      if (!trimmed || trimmed === "{}" || trimmed === "[]") return null;
      const colonIdx = trimmed.indexOf(":");
      if (colonIdx > -1 && colonIdx < 35 && !trimmed.startsWith("http")) {
        return {
          title: trimmed.substring(0, colonIdx).trim(),
          description: "",
          text: trimmed.substring(colonIdx + 1).trim(),
          _id: null,
        };
      }
      return { title: trimmed, description: "", text: trimmed, _id: null };
    }

    return null;
  };

  const parseSuggestions = (arr) => {
    if (!arr) return emptyPresets();
    let rawList = arr;
    if (typeof rawList === "string") {
      const parsed = robustJsonParse(rawList);
      if (Array.isArray(parsed)) rawList = parsed;
      else if (parsed && typeof parsed === "object") rawList = [parsed];
    }
    if (!Array.isArray(rawList)) rawList = [rawList];

    const flattened = rawList.flatMap(unwrapPresetValue).filter(Boolean);

    // Deduplicate
    const seen = new Set();
    const deduplicated = [];
    for (const item of flattened) {
      if (!item.title && !item.description && !item.text) continue;
      const key = `${(item.title || "").toLowerCase()}|||${(item.description || "").toLowerCase()}|||${(item.text || "").toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduplicated.push(item);
      }
    }

    return deduplicated;
  };

  const parseFramework = (data) => {
    if (!data) return [];
    const normalize = (item) => {
      if (!item) return null;
      if (typeof item === "string") {
        try {
          const parsedItem = JSON.parse(item);
          if (parsedItem && typeof parsedItem === "object") {
            return normalize(parsedItem);
          }
        } catch (_) { }
        const colonIdx = item.indexOf(":");
        if (colonIdx > -1) {
          return {
            title: item.substring(0, colonIdx).replace(/^[•\-*\s]+/, "").trim(),
            description: item.substring(colonIdx + 1).trim(),
            _id: null,
          };
        }
        return {
          title: "",
          description: item.replace(/^[•\-*\s]+/, "").trim(),
          _id: null,
        };
      }
      if (typeof item === "object") {
        const title = item.title || item.key || item.name || "";
        const description = item.description || item.text || item.value || "";
        return {
          title: String(title).trim(),
          description: String(description).trim(),
          _id: item._id || null,
        };
      }
      return null;
    };

    if (Array.isArray(data)) {
      return data.map(normalize).filter(Boolean);
    }
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return parsed.map(normalize).filter(Boolean);
        }
        if (parsed && typeof parsed === "object") {
          return [normalize(parsed)].filter(Boolean);
        }
      } catch (_) {
        const lines = data.split("\n").filter((l) => l.trim().length > 0);
        if (lines.length > 0) {
          return lines.map((line) => normalize(line)).filter(Boolean);
        }
      }
    }
    return [];
  };

  useEffect(() => { getAllGroups({ showLoader: true }); }, []);
  useEffect(() => { dispatch(setInitial(true)); }, []);

  useEffect(() => {
    const handleClickOutside = () => { setIsOpen(false); setGroupSearch(""); };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!Array.isArray(groups) || !groups.length) return;
    const match =
      groups.find((g) => g.groupName === selectedGroup) ||
      (groupIdRef.current && groups.find((g) => String(g._id) === String(groupIdRef.current)));
    if (match) {
      setGroupId(match._id);
      setGroupData(match);
      setIsEdit(true);
    } else if (!selectedGroup) {
      setSelectedGroup(groups[0]?.groupName || "");
    }
  }, [selectedGroup, groups]);

  useEffect(() => {
    if (!groupId || !groupData || groupData._id !== groupId) return;
    setPresets({
      transcription: parseSuggestions(groupData?.transcribe ?? groupData?.suggestion),
      translation: parseSuggestions(groupData?.translate),
      aiChat: parseSuggestions(groupData?.chat),
    });
    setImagePreview(groupData?.groupImage);
    setTrainingImg("");
    setMasterPrompt(groupData?.masterPrompt || "");
    setGroupStatus(groupData?.status !== false);
    setFrameworkItems(parseFramework(groupData?.aiFramework || groupData?.framework || groupData?.ai_framework || groupData?.frameworkItems));
    setPromptSaved(true);
  }, [groupId, groupData]);

  const openDeleteGroupModal = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const closeDeleteGroupModal = () => {
    setShowDeleteModal(false);
    setGroupToDelete(null);
  };

  const handleConfirmDeleteGroup = async () => {
    if (!groupToDelete?._id) return;
    try {
      setIsDeleting(true);
      const response = await TrainingService?.deleteGroup(groupToDelete._id);
      if (response) {
        toastSuccess(t.toastDeleted);
        if (groupId === groupToDelete._id) {
          setGroupId("");
          setGroupData(null);
          setSelectedGroup("");
        }
        closeDeleteGroupModal();
        await getAllGroups({ showLoader: false, showListLoader: true });
      }
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to delete group");
    } finally {
      setIsDeleting(false);
    }
  };

  const isEmptyPresets = !currentPresets || currentPresets.length === 0;
  const filledCount = (tab) =>
    (presets[tab] || []).filter(
      (s) =>
        (s?.text && typeof s.text === "string" && s.text.trim() !== "") ||
        (s?.title && typeof s.title === "string" && s.title.trim() !== "") ||
        (s?.description && typeof s.description === "string" && s.description.trim() !== "")
    ).length;
  const filteredGroups = (Array.isArray(groups) ? groups : []).filter((g) =>
    g.groupName?.toLowerCase().includes(groupSearch.toLowerCase())
  );

  // Handle image error
  const handleImageError = (groupId) => {
    setImageErrors(prev => ({ ...prev, [groupId]: true }));
  };

  if (loading && (!groups || groups.length === 0)) {
    return <AiTrainingSkeleton t={t} />;
  }

  return (
    <div className="flex flex-col w-full h-full p-3 lg:p-4 overflow-hidden font-roboto bg-[#F4F4F8]">
      <div className="flex flex-col lg:flex-row gap-3.5 lg:gap-4 flex-1 min-h-0 overflow-hidden">

        {/* ============ LEFT: white console rail ============ */}
        <aside className="lg:w-[260px] xl:w-[290px] 2xl:w-[320px] flex-shrink-0 flex flex-col bg-white rounded-[24px] shadow-sm overflow-hidden h-full">
          {/* Brand strip */}
          <div className="flex items-center gap-3 px-5 lg:px-6 pt-5 lg:pt-6 pb-4 lg:pb-5">
            <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/20 ring-4 ring-primary/10 flex-shrink-0">
              <BsStars className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-sm lg:text-base text-gray-800 tracking-tight truncate">{t.title}</h1>
            </div>
          </div>

          {/* New group action */}
          <div className="px-4 lg:px-6">
            <button
              type="button"
              onClick={() => { setShowNewGroupPopup(true); setIsOpen(false); setMasterPrompt(""); setFrameworkItems([]); setNewGroupErrors({}); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 lg:py-3 rounded-full bg-primary text-white font-semibold text-xs lg:text-sm hover:bg-primary/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-primary/20"
            >
              <FiPlus className="w-4 h-4" />
              {t.newGroup}
            </button>
          </div>

          {/* Selected group / logo */}
          <div className="px-4 lg:px-6 mt-3 lg:mt-4">
            <div className="flex items-center gap-2.5 lg:gap-3 mb-3">
              <label
                htmlFor="upload"
                className={`relative w-12 h-12 lg:w-14 lg:h-14 flex-shrink-0 cursor-pointer rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 group shadow-inner bg-gradient-to-br from-[#F4F1FC] via-[#E9E4F8] to-[#DDD6F4] border-2 ${imagePreview
                  ? "border-primary/40 ring-2 ring-primary/30 ring-offset-2 ring-offset-white"
                  : "border-dashed border-primary/30 hover:border-primary/60"
                  }`}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Group"
                    className="h-full w-full object-contain p-2 drop-shadow-[0_2px_4px_rgba(83,70,148,0.25)]"
                    onError={() => setImagePreview(null)}
                  />
                ) : selectedGroup ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <HiOutlineUserGroup className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                  </div>
                ) : (
                  <MdUpload className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors" />
                )}
                {imagePreview && (
                  <div className="absolute inset-0 bg-primary/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-xs">
                    <span className="text-white text-[9px] font-semibold">Change</span>
                  </div>
                )}
              </label>
              <input type="file" id="upload" hidden accept="image/*" onChange={handleImageUpload} />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] lg:text-xs text-gray-400">{t.selectGroup}</p>
                <p className="text-xs lg:text-sm font-semibold text-gray-800 truncate">
                  {selectedGroup || t.selectGroupPlaceholder}
                </p>

              </div>
            </div>
          </div>

          {/* Search */}
          <div className="px-4 lg:px-6 mt-2 lg:mt-4">
            <div className="relative">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-lg" />
              <input
                type="text"
                value={groupSearch}
                onClick={(e) => { e.stopPropagation(); if (!isOpen) setIsOpen(true); }}
                onChange={(e) => { setGroupSearch(e.target.value); setIsOpen(true); }}
                placeholder={t.searchGroups}
                className="w-full pl-10 pr-3 py-2 lg:py-2.5 rounded-full bg-[#F4F4F8] border border-gray-200 text-xs lg:text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all"
              />
            </div>
          </div>

          {/* Group list */}
          <div className="px-3 mt-3 lg:mt-4 flex-1 overflow-y-auto min-h-[120px] custom-scrollbar" onClick={(e) => e.stopPropagation()}>
            <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-gray-300">
              {t.groups} · {filteredGroups.length}
            </p>
            {groupsListLoading ? (
              <div className="space-y-1.5 pb-4 select-none">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-gray-50/80">
                    <div className="w-9 h-9 rounded-full skeleton-bone flex-shrink-0" />
                    <div
                      className="h-4 rounded-md skeleton-bone flex-1"
                      style={{ maxWidth: i === 1 ? "120px" : i === 2 ? "90px" : "110px" }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-1 pb-4">
                {filteredGroups?.map((option) => {
                  const active = selectedGroup === option.groupName;
                  const hasImageError = imageErrors[option._id];
                  const hasValidImage = option?.groupImage && !hasImageError;

                  return (
                    <li
                      key={option._id}
                      onClick={() => handleGroupSelect(option)}
                      className={`group flex items-center gap-3 px-3 py-2 lg:py-2.5 rounded-2xl cursor-pointer transition-all duration-150 ${active ? "bg-primary/[0.07]" : "hover:bg-gray-100/60"
                        }`}
                    >
                      <div className={`relative w-8 h-8 lg:w-9 lg:h-9 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#F4F1FC] to-[#DDD6F4] border border-primary/15 shadow-2xs ${active ? "ring-2 ring-primary ring-offset-2 ring-offset-white" : ""
                        }`}>
                        {hasValidImage ? (
                          <img
                            src={option.groupImage}
                            alt={option.groupName}
                            className="w-full h-full object-contain p-1.5 drop-shadow-[0_1px_3px_rgba(83,70,148,0.25)]"
                            onError={() => handleImageError(option._id)}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <HiOutlineUserGroup className="w-4 h-4 text-primary" />
                          </div>
                        )}
                      </div>
                      <span className={`flex-1 min-w-0 truncate text-xs lg:text-sm ${active ? "text-gray-800 font-semibold" : "text-gray-500"
                        }`}>
                        {option?.groupName}
                      </span>
                      {active && <MdCheck className="text-primary flex-shrink-0 text-base lg:text-lg" />}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openDeleteGroupModal(option); }}
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:bg-rose-50 hover:text-rose-500 transition-all duration-200"
                        title={t.deleteGroup}
                      >
                        <MdDeleteOutline className="text-sm" />
                      </button>
                    </li>
                  );
                })}
                {filteredGroups?.length === 0 && (
                  <li className="px-3 py-6 text-center text-xs lg:text-sm text-gray-300">{t.noGroupsFound}</li>
                )}
              </ul>
            )}
          </div>
        </aside>

        {/* ============ RIGHT: main content ============ */}
        <main className="flex-1 flex flex-col gap-2.5 lg:gap-3 min-w-0 h-full overflow-hidden">

          {/* System prompt card */}
          <div className="bg-white rounded-[20px] lg:rounded-[22px] shadow-sm overflow-hidden flex-shrink-0">
            <div className="flex items-center justify-between px-4 lg:px-5 py-2.5 lg:py-3">
              <div className="flex items-center gap-2 lg:gap-2.5">
                <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FiCpu className="text-primary text-xs lg:text-sm" />
                </div>
                <div>
                  <h3 className="font-bold text-xs lg:text-sm text-gray-800">{t.masterPrompt}</h3>
                  <p className="text-[10px] lg:text-[11px] text-gray-400">{t.masterPromptHint}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowPromptModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 active:scale-95 transition-all duration-200 flex-shrink-0"
                title={t.expandPromptHint || "Expand prompt editor"}
              >
                <FiMaximize2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline text-xs font-medium">{t.expandPrompt || "Expand"}</span>
              </button>
            </div>
            <div className="px-4 lg:px-5 pb-3">
              <textarea
                value={masterPrompt}
                onChange={(e) => {
                  setMasterPrompt(e.target.value);
                  setPromptSaved(false);
                }}
                placeholder={t.masterPromptPlaceholder}
                rows={2}
                className="w-full rounded-xl border border-gray-200 bg-[#F7F7FA] px-3.5 py-2 text-xs lg:text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 resize-none transition-all custom-scrollbar"
              />
            </div>
          </div>

          {/* AI Framework card */}
          <div className="bg-white rounded-[20px] lg:rounded-[22px] shadow-sm overflow-hidden flex flex-col flex-1 min-h-[130px]">
            <div className="flex items-center justify-between px-4 lg:px-5 py-2.5 lg:py-3 flex-shrink-0">
              <div className="flex items-center gap-2 lg:gap-2.5 min-w-0">
                <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FiLayers className="text-primary text-xs lg:text-sm" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-xs lg:text-sm text-gray-800 truncate">{t.aiFramework}</h3>
                  <p className="text-[10px] lg:text-[11px] text-gray-400 truncate">{t.aiFrameworkHint}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
                {/* Status Switch Toggle */}
                <button
                  type="button"
                  onClick={handleToggleGroupStatus}
                  className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 transition-all duration-200 ease-in-out focus:outline-none ${groupStatus
                    ? "bg-primary border-primary shadow-xs"
                    : "bg-slate-300 border-slate-300 hover:bg-slate-400"
                    }`}
                  title={groupStatus ? t.active : t.inactive}
                  aria-label={groupStatus ? t.active : t.inactive}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${groupStatus ? "translate-x-5" : "translate-x-0"
                      }`}
                  />
                </button>

                <button
                  type="button"
                  onClick={handleAddFrameworkItem}
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 xl:px-3.5 xl:py-1.5 rounded-full bg-primary text-white font-semibold text-xs hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-sm shadow-primary/20 flex-shrink-0"
                  title={t.addRule}
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span className="hidden xl:inline">{t.addRule}</span>
                </button>
              </div>
            </div>

            <div className="px-4 lg:px-5 pb-3 pt-0.5 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
              {frameworkItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-4 gap-2 border-2 border-dashed border-gray-100 rounded-2xl bg-[#FAFAFC] h-full">
                  <p className="font-semibold text-xs text-gray-500">{t.noFramework}</p>
                  <button
                    type="button"
                    onClick={handleAddFrameworkItem}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-primary/25 text-primary font-semibold text-xs hover:bg-primary/5 transition-all"
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    {t.addRule}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {frameworkItems.map((item, index) => {
                    return (
                      <div
                        key={item._id || index}
                        className="group flex items-start gap-3 p-3 rounded-2xl border border-gray-100 bg-[#FAFAFC] hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-200"
                      >
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>

                        <div className="flex-1 min-w-0 flex flex-col gap-0.5 py-0.5">
                          <h4 className="text-xs font-bold text-gray-800 leading-normal break-words">
                            {item.title || <span className="italic font-normal text-gray-400">Untitled Rule</span>}
                          </h4>
                          <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed break-words">
                            {item.description || <span className="italic text-gray-400">No description</span>}
                          </p>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                          {/* Edit Button */}
                          <button
                            type="button"
                            onClick={() => openEditRuleModal(index)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/10 transition-all duration-200"
                            title={t.editRule}
                          >
                            <FiEdit2 className="text-xs" />
                          </button>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveFrameworkItem(index)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-all duration-200"
                            title="Delete rule"
                          >
                            <MdDeleteOutline className="text-base" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Presets card */}
          <div className="bg-white rounded-[20px] lg:rounded-[22px] shadow-sm overflow-hidden flex flex-col flex-1 min-h-[150px]">
            <div className="flex items-center justify-between px-4 lg:px-5 py-2.5 lg:py-3 flex-shrink-0">
              <div className="flex items-center gap-2 lg:gap-2.5 min-w-0">
                <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BsLayoutTextSidebarReverse className="text-primary text-xs lg:text-sm" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-xs lg:text-sm text-gray-800 truncate">
                    {t.presetsFor}{" "}
                    <span className="text-primary">
                      {lang === "EN"
                        ? TABS.find((tb) => tb.key === activeTab)?.labelEN
                        : TABS.find((tb) => tb.key === activeTab)?.labelES}
                    </span>
                  </h2>
                  <p className="text-[10px] lg:text-[11px] text-gray-400 truncate">
                    {filledCount(activeTab)} workmode{filledCount(activeTab) !== 1 ? "s" : ""} added
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddPreset}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 xl:px-4 xl:py-1.5 rounded-full bg-primary text-white font-semibold text-xs hover:bg-primary/90 active:scale-95 transition-all duration-200 flex-shrink-0 shadow-sm shadow-primary/20"
                title={t.addPreset}
              >
                <FiPlus className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">{t.addPreset}</span>
              </button>
            </div>

            {/* Segmented pill tabs */}
            <div className="px-4 lg:px-5 pb-2 flex-shrink-0 overflow-x-auto custom-scrollbar">
              <div className="inline-flex p-1 rounded-full bg-[#F1F1F5] gap-1">
                {TABS.map(({ key, labelEN, labelES, icon, dot }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-1.5 px-3 lg:px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === key
                      ? "bg-white text-gray-800 shadow-xs"
                      : "text-gray-400 hover:text-gray-600"
                      }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${activeTab === key ? dot : "bg-gray-300"}`} />
                    {icon}
                    {lang === "EN" ? labelEN : labelES}
                  </button>
                ))}
              </div>
            </div>

            {/* Preset list */}
            <div
              id="presets-scroll-container"
              className="flex-1 min-h-0 overflow-y-auto px-4 lg:px-5 pb-3 pt-0.5 custom-scrollbar"
            >
              {isEmptyPresets ? (
                <div className="flex flex-col items-center justify-center h-full py-4 gap-2">
                  <div className="text-center">
                    <p className="font-semibold text-xs text-gray-500">{t.noPresets}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{t.noPresetsHint}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPreset}
                    className="mt-0.5 flex items-center gap-1.5 px-4 py-1.5 rounded-full border-2 border-dashed border-primary/25 text-primary font-semibold text-xs hover:bg-primary/5 hover:border-primary/50 transition-all"
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    {t.addPreset}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5 pr-1">
                  {currentPresets.map((suggestion, index) => (
                    <div
                      key={suggestion._id || index}
                      className="group flex items-start gap-3 p-3 rounded-2xl border border-gray-100 bg-[#FAFAFC] hover:border-primary/20 hover:bg-white hover:shadow-sm transition-all duration-200"
                    >
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>

                      <div className="flex-1 min-w-0 flex flex-col gap-0.5 py-0.5">
                        {(suggestion.title || suggestion.description) ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {suggestion.title && (
                              <h4 className="text-xs font-bold text-gray-800 leading-normal break-words">
                                {suggestion.title}
                              </h4>
                            )}
                            {suggestion.description && (
                              <span className="text-[11px] text-gray-500 font-normal leading-normal">
                                {suggestion.title ? `· ${suggestion.description}` : suggestion.description}
                              </span>
                            )}
                          </div>
                        ) : !suggestion.text ? (
                          <h4 className="text-xs font-normal italic text-gray-400 leading-normal">
                            Workmode {index + 1}
                          </h4>
                        ) : null}

                        {suggestion.text && (
                          <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed break-words">
                            {suggestion.text}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                        {/* Edit Button */}
                        <button
                          type="button"
                          onClick={() => openEditPresetModal(index)}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/10 transition-all duration-200"
                          title={t.editPreset}
                        >
                          <FiEdit2 className="text-xs" />
                        </button>

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={() => handleRemovePreset(index)}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-all duration-200"
                          title="Delete workmode"
                        >
                          <MdDeleteOutline className="text-base" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Save action */}
          <div className="flex items-center justify-end flex-shrink-0 pt-0.5">
            <button
              onClick={handleSubmit}
              disabled={isSubmiting}
              className="flex items-center justify-center gap-2 px-8 py-2.5 rounded-full font-bold text-xs md:text-sm text-white bg-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all duration-300 min-w-[150px] disabled:opacity-70"
            >
              {isSubmiting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t.saving}
                </>
              ) : (
                isEdit ? t.updateGroup : t.createGroup
              )}
            </button>
          </div>
        </main>
      </div>

      {/* ============ New Group Modal ============ */}
      {showNewGroupPopup && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn"
          onClick={closeNewGroupPopup}
        >
          <div
            className="bg-white rounded-[28px] shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
                  <FiPlus className="w-5 h-5 text-white" />
                </div>
                <h2 className="font-bold text-lg text-gray-800">{t.newGroup}</h2>
              </div>
              <button
                onClick={closeNewGroupPopup}
                className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-primary transition-colors"
              >
                <IoMdClose className="text-xl" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
              <div className="flex flex-col items-center gap-3">
                <label
                  htmlFor="popup-upload"
                  className={`relative w-24 h-24 cursor-pointer rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 group shadow-inner bg-gradient-to-br from-[#F4F1FC] via-[#E9E4F8] to-[#DDD6F4] border-2 ${popupLogoPreview
                    ? "border-primary/40 ring-2 ring-primary/30 ring-offset-2 ring-offset-white"
                    : "border-dashed border-primary/30 hover:border-primary/60 hover:bg-[#E5DFFA]"
                    }`}
                >
                  {popupLogoPreview ? (
                    <img
                      src={popupLogoPreview}
                      alt="Group"
                      className="h-full w-full object-contain p-2.5 drop-shadow-[0_2px_6px_rgba(83,70,148,0.25)]"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <MdUpload className="w-7 h-7 text-primary/60 group-hover:text-primary transition-colors" />
                      <span className="text-[10px] text-primary/70 font-semibold uppercase tracking-wider">Upload</span>
                    </div>
                  )}
                </label>
                <input type="file" id="popup-upload" hidden accept="image/*" onChange={handlePopupLogoUpload} />
                {popupLogoPreview && (
                  <button
                    type="button"
                    onClick={() => { setPopupLogo(""); setPopupLogoPreview(null); }}
                    className="text-xs text-rose-400 hover:text-rose-600 transition-colors"
                  >
                    {t.removeLogo}
                  </button>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  {t.groupName} <span className="text-red-500">*</span>
                </label>
                <input
                  autoFocus
                  type="text"
                  value={addGroupName}
                  onChange={(e) => {
                    setAddGroupName(e.target.value);
                    if (e.target.value.trim()) setNewGroupErrors((prev) => ({ ...prev, groupName: "" }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleConfirmNewGroup();
                    if (e.key === "Escape") closeNewGroupPopup();
                  }}
                  placeholder={t.typeGroupPlaceholder}
                  className={`w-full px-4 py-3 rounded-2xl border bg-[#F7F7FA] text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all ${newGroupErrors.groupName ? "border-red-400 ring-1 ring-red-400" : "border-gray-200"
                    }`}
                />
                {newGroupErrors.groupName && (
                  <p className="text-xs text-red-500 mt-1">{newGroupErrors.groupName}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  {t.masterPrompt}
                </label>
                <textarea
                  value={masterPrompt}
                  onChange={(e) => {
                    setMasterPrompt(e.target.value);
                  }}
                  placeholder={t.masterPromptPlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-[#F7F7FA] text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 resize-none transition-all custom-scrollbar"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t.aiFramework}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddFrameworkItem}
                    className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
                  >
                    <FiPlus /> {t.addRule}
                  </button>
                </div>
                {frameworkItems.length === 0 ? (
                  <div className="p-3 text-center border border-dashed border-gray-200 rounded-2xl bg-[#F7F7FA]">
                    <p className="text-xs text-gray-400">{t.noFramework}</p>
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                    {frameworkItems.map((item, index) => {
                      return (
                        <div
                          key={item._id || index}
                          className="p-3 rounded-xl border border-gray-100 bg-[#F7F7FA] flex items-start gap-2.5 hover:bg-white transition-all"
                        >
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5 bg-primary/10 text-primary">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <h5 className="text-xs font-bold truncate text-gray-800">
                              {item.title || "Untitled Rule"}
                            </h5>
                            <p className="text-[11px] text-gray-600 line-clamp-2">{item.description}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => openEditRuleModal(index)}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                              title={t.editRule}
                            >
                              <FiEdit2 className="text-[10px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveFrameworkItem(index)}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                              title="Delete rule"
                            >
                              <MdDeleteOutline className="text-sm" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={closeNewGroupPopup}
                className="px-6 py-3 rounded-full font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmNewGroup}
                disabled={!addGroupName.trim() || newGroupCreating}
                className="px-8 py-3 rounded-full font-bold text-sm text-white bg-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-60 min-w-[120px] flex items-center justify-center"
              >
                {newGroupCreating ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t.create
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteModal && groupToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn"
          onClick={closeDeleteGroupModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-red-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b border-gray-100 px-6 py-4 bg-gradient-to-r from-red-50/50 via-white to-red-50/50">
              <h2 className="font-roboto font-bold text-base text-red-500 uppercase tracking-wide">
                {t.deleteGroup}
              </h2>
              <button
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={closeDeleteGroupModal}
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            <div className="p-6 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center text-red-500 mb-1">
                <RiErrorWarningLine className="text-3xl" />
              </div>
              <h3 className="font-roboto font-bold text-lg text-gray-800">
                {lang === "ES" ? "¿Estás seguro?" : "Are you sure?"}
              </h3>
              <p className="font-roboto text-sm text-gray-500 max-w-xs">
                {lang === "ES" ? "Estás a punto de eliminar el grupo " : "You are about to delete "}
                <span className="font-bold text-gray-800">"{groupToDelete.groupName}"</span>
                {lang === "ES" ? ". Esta acción no se puede deshacer." : ". This action cannot be undone."}
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                className="px-5 py-2.5 rounded-xl font-roboto font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
                onClick={closeDeleteGroupModal}
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteGroup}
                disabled={isDeleting}
                className="px-6 py-2.5 rounded-xl font-roboto font-bold text-sm text-white bg-red-500 hover:bg-red-600 transition-all uppercase tracking-wide disabled:opacity-60 min-w-[100px] flex items-center justify-center shadow-sm"
              >
                {isDeleting ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t.deleteConfirm || t.deleteGroup
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Framework Rule Modal */}
      {showRuleModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn"
          onClick={closeRuleModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-gray-100 px-6 py-4 bg-gradient-to-r from-primary/5 via-white to-primary/5">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <FiLayers className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="font-roboto font-bold text-base text-gray-800">
                    {editingRuleIndex !== null ? t.editRuleModalTitle : t.addRuleModalTitle}
                  </h2>
                  <p className="text-[11px] text-gray-400">
                    {lang === "ES" ? "Configura las pautas y estándares del marco" : "Configure AI framework standards & instructions"}
                  </p>
                </div>
              </div>
              <button
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={closeRuleModal}
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveRuleModal} className="p-6 space-y-4">
              {/* Title input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  {t.ruleTitle} <span className="text-rose-500">*</span>
                </label>
                <input
                  autoFocus
                  type="text"
                  value={ruleForm.title}
                  onChange={(e) => {
                    setRuleForm((prev) => ({ ...prev, title: e.target.value }));
                    if (ruleFormErrors.title) setRuleFormErrors((prev) => ({ ...prev, title: "" }));
                  }}
                  placeholder={t.ruleTitlePlaceholder}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none transition-all ${ruleFormErrors.title
                    ? "border-rose-400 bg-rose-50/20 focus:ring-2 focus:ring-rose-200"
                    : "border-gray-200 bg-[#F7F7FA] focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary/30"
                    }`}
                />
                {ruleFormErrors.title && (
                  <p className="text-rose-500 text-xs mt-1 font-medium">{ruleFormErrors.title}</p>
                )}
              </div>

              {/* Description textarea */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  {t.ruleDescription} <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={ruleForm.description}
                  onChange={(e) => {
                    setRuleForm((prev) => ({ ...prev, description: e.target.value }));
                    if (ruleFormErrors.description) setRuleFormErrors((prev) => ({ ...prev, description: "" }));
                  }}
                  placeholder={t.ruleDescPlaceholder}
                  rows={4}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none transition-all resize-none custom-scrollbar ${ruleFormErrors.description
                    ? "border-rose-400 bg-rose-50/20 focus:ring-2 focus:ring-rose-200"
                    : "border-gray-200 bg-[#F7F7FA] focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary/30"
                    }`}
                />
                {ruleFormErrors.description && (
                  <p className="text-rose-500 text-xs mt-1 font-medium">{ruleFormErrors.description}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeRuleModal}
                  disabled={savingRule}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={savingRule}
                  className="px-6 py-2.5 rounded-full font-bold text-sm text-white bg-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all shadow-md shadow-primary/20 flex items-center gap-1.5 disabled:opacity-70 min-w-[110px] justify-center"
                >
                  {savingRule ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <MdCheck className="text-base" />
                      {editingRuleIndex !== null ? t.saveRule : t.addRule}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============ Preset Modal (Add / Edit) ============ */}
      {showPresetModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn"
          onClick={closePresetModal}
        >
          <div
            className="bg-white rounded-[28px] shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100 overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <BsLayoutTextSidebarReverse className="text-primary text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">
                    {editingPresetIndex !== null ? (t.editPresetModalTitle || "Edit Workmode Suggestion") : (t.addPresetModalTitle || "Add Workmode Suggestion")}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {lang === "EN"
                      ? `Configure workmode for ${TABS.find((tb) => tb.key === activeTab)?.labelEN}`
                      : `Configura workmode para ${TABS.find((tb) => tb.key === activeTab)?.labelES}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePresetModal}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <form onSubmit={handleSavePresetModal} className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1">
              {/* Title field */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  {t.presetTitle || "Workmode Title"}
                </label>
                <input
                  type="text"
                  value={presetForm.title}
                  onChange={(e) => setPresetForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder={t.presetTitlePlaceholder}
                  className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 bg-[#F7F7FA] focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary/30 text-sm text-gray-700 transition-all"
                />
              </div>

              {/* Description field */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  {t.presetDescription || "Description"}
                </label>
                <input
                  type="text"
                  value={presetForm.description}
                  onChange={(e) => setPresetForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t.presetDescPlaceholder}
                  className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 bg-[#F7F7FA] focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary/30 text-sm text-gray-700 transition-all"
                />
              </div>

              {/* Prompt Text field */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  {t.presetPrompt || "Prompt Text"} <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={presetForm.text}
                  onChange={(e) => {
                    setPresetForm((prev) => ({ ...prev, text: e.target.value }));
                    if (presetFormErrors.text) setPresetFormErrors((prev) => ({ ...prev, text: null }));
                  }}
                  placeholder={t.presetTextPlaceholder}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm text-gray-700 transition-all resize-none custom-scrollbar ${presetFormErrors.text
                    ? "border-rose-400 bg-rose-50/20 focus:ring-2 focus:ring-rose-200"
                    : "border-gray-200 bg-[#F7F7FA] focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary/30"
                    }`}
                />
                {presetFormErrors.text && (
                  <p className="text-rose-500 text-xs mt-1 font-medium">{presetFormErrors.text}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closePresetModal}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={savingPreset}
                  className="px-6 py-2.5 rounded-full font-bold text-sm text-white bg-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all shadow-md shadow-primary/20 flex items-center gap-1.5 min-w-[110px] justify-center disabled:opacity-70"
                >
                  {savingPreset ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <MdCheck className="text-base" />
                      {editingPresetIndex !== null ? (t.savePreset || "Save Workmode") : (t.addPreset || "Add Workmode")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expanded System Prompt Modal */}
      {showPromptModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn"
          onClick={() => setShowPromptModal(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden border border-gray-100 flex flex-col max-h-[88vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-gray-100 px-6 py-4 bg-gradient-to-r from-primary/5 via-white to-primary/5 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  <FiCpu className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="font-roboto font-bold text-base text-gray-800">
                    {t.promptModalTitle || t.masterPrompt}
                  </h2>
                  <p className="text-[11px] text-gray-400">
                    {t.promptModalSubtitle || t.masterPromptHint}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={() => setShowPromptModal(false)}
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 flex flex-col flex-1 min-h-0 gap-3">
              <div className="flex-1 min-h-[300px] flex flex-col">
                <textarea
                  value={masterPrompt}
                  onChange={(e) => {
                    setMasterPrompt(e.target.value);
                    setPromptSaved(false);
                  }}
                  placeholder={t.masterPromptPlaceholder}
                  className="w-full h-full min-h-[320px] rounded-2xl border border-gray-200 bg-[#F7F7FA] focus:bg-white p-4 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 resize-none transition-all custom-scrollbar leading-relaxed"
                />
              </div>

              {/* Stats & Actions */}
              <div className="flex items-center justify-end pt-1 text-xs text-gray-400 flex-shrink-0">


                <button
                  type="button"
                  onClick={() => setShowPromptModal(false)}
                  className="flex items-center gap-1.5 px-6 py-2 rounded-full font-bold text-sm text-white bg-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all shadow-md shadow-primary/20"
                >
                  <MdCheck className="text-base" />
                  {t.done || "Done"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </div>
  );
};

/* ── AI Training Workspace Shimmer Skeleton ─────────────────────── */
const AiTrainingSkeleton = () => {
  return (
    <div className="flex flex-col w-full h-full p-3 lg:p-4 overflow-hidden font-roboto bg-[#F4F4F8] select-none">
      <div className="flex flex-col lg:flex-row gap-3.5 lg:gap-4 flex-1 min-h-0 overflow-hidden">
        {/* Left Console Rail Skeleton */}
        <aside className="lg:w-[300px] xl:w-[320px] flex-shrink-0 flex flex-col bg-white rounded-[24px] shadow-sm overflow-hidden p-5 gap-4 h-full">
          {/* Brand strip */}
          <div className="flex items-center gap-2.5 pb-1">
            <div className="w-9 h-9 rounded-full skeleton-bone-primary flex-shrink-0" />
            <div className="w-28 h-4 rounded-lg skeleton-bone" />
          </div>

          {/* New group action button */}
          <div className="w-full h-10 rounded-full skeleton-bone-primary" />

          {/* Selected group card preview */}
          <div className="flex items-center gap-3 py-1">
            <div className="w-12 h-12 rounded-full skeleton-bone flex-shrink-0" />
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="w-16 h-2.5 rounded-md skeleton-bone" />
              <div className="w-24 h-3.5 rounded-md skeleton-bone" />
            </div>
          </div>

          {/* Search input placeholder */}
          <div className="w-full h-9 rounded-full bg-[#F4F4F8] flex items-center px-3.5 gap-2">
            <div className="w-3.5 h-3.5 rounded-full skeleton-bone flex-shrink-0" />
            <div className="w-20 h-3 rounded-md skeleton-bone" />
          </div>

          {/* Groups list */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-2 pt-1">
            <div className="w-16 h-2.5 rounded-md skeleton-bone" />
            <div className="flex flex-col gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 p-2 rounded-2xl bg-gray-50/80"
                >
                  <div className="w-8 h-8 rounded-full skeleton-bone flex-shrink-0" />
                  <div
                    className="h-3.5 rounded-md skeleton-bone flex-1"
                    style={{ maxWidth: i === 1 ? "110px" : i === 2 ? "80px" : i === 3 ? "100px" : "70px" }}
                  />
                  <div className="w-6 h-6 rounded-full bg-gray-100/60 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Right Main Content Skeleton */}
        <main className="flex-1 flex flex-col gap-3 min-w-0 h-full overflow-hidden">
          {/* System prompt card */}
          <div className="bg-white rounded-[22px] shadow-sm overflow-hidden p-4 flex flex-col gap-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full skeleton-bone-primary flex-shrink-0" />
                <div>
                  <div className="w-28 h-3.5 rounded-md skeleton-bone" />
                  <div className="w-40 h-2.5 rounded-md skeleton-bone mt-1.5" />
                </div>
              </div>
            </div>
            <div className="w-full h-16 rounded-xl bg-[#F7F7FA] p-3 flex flex-col gap-2">
              <div className="w-56 h-3 rounded-md skeleton-bone" />
              <div className="w-72 h-3 rounded-md skeleton-bone" />
            </div>
          </div>

          {/* AI Framework card */}
          <div className="bg-white rounded-[22px] shadow-sm overflow-hidden p-4 flex flex-col flex-1 min-h-[140px]">
            <div className="flex items-center justify-between pb-2 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full skeleton-bone-primary flex-shrink-0" />
                <div>
                  <div className="w-28 h-3.5 rounded-md skeleton-bone" />
                  <div className="w-40 h-2.5 rounded-md skeleton-bone mt-1.5" />
                </div>
              </div>
              <div className="w-20 h-7 rounded-full skeleton-bone-primary" />
            </div>
            <div className="space-y-2 flex-1 min-h-0 overflow-hidden pt-1">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#FAFAFC]">
                  <div className="w-6 h-6 rounded-full skeleton-bone-primary flex-shrink-0" />
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="w-28 h-3 rounded-md skeleton-bone" />
                    <div className="w-44 h-2.5 rounded-md skeleton-bone" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Presets card */}
          <div className="bg-white rounded-[22px] shadow-sm overflow-hidden p-4 flex flex-col flex-1 min-h-[160px]">
            <div className="flex items-center justify-between pb-2 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full skeleton-bone-primary flex-shrink-0" />
                <div>
                  <div className="w-32 h-3.5 rounded-md skeleton-bone" />
                  <div className="w-20 h-2.5 rounded-md skeleton-bone mt-1.5" />
                </div>
              </div>
              <div className="w-24 h-7 rounded-full skeleton-bone-primary" />
            </div>

            {/* Tabs */}
            <div className="pb-2 flex-shrink-0">
              <div className="inline-flex p-1 rounded-full bg-[#F1F1F5] gap-1">
                <div className="w-24 h-7 rounded-full bg-white shadow-xs flex items-center justify-center">
                  <div className="w-16 h-2.5 rounded-md skeleton-bone" />
                </div>
                <div className="w-20 h-7 rounded-full flex items-center justify-center">
                  <div className="w-12 h-2.5 rounded-md skeleton-bone opacity-60" />
                </div>
              </div>
            </div>

            {/* Presets Rows */}
            <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-hidden pt-1">
              {[
                { width: "70%" },
                { width: "45%" },
              ].map((row, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#FAFAFC]"
                >
                  <div className="w-6 h-6 rounded-full skeleton-bone-primary flex items-center justify-center font-bold text-xs text-primary/40 flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div
                    className="h-3.5 rounded-md skeleton-bone"
                    style={{ width: row.width }}
                  />
                  <div className="ml-auto w-6 h-6 rounded-full bg-gray-100/60 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>

          {/* Bottom button */}
          <div className="flex justify-end pt-0.5 flex-shrink-0">
            <div className="w-32 h-9 rounded-full skeleton-bone-primary" />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AiTraining;